/* FrontRow Tech inner pages and blog behaviours (M4).
   Progressive only: every page reads and works without this file. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function all(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function slugify(text) {
    return String(text).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'section';
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  function fill(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, function (_, key) {
      return values[key] != null ? values[key] : '';
    });
  }

  /* ---------- Reveal on scroll (soft, staggered, motion-safe) ---------- */
  function initReveal() {
    var groups = all('[data-frp-stagger]');
    var singles = all('.frp-reveal');
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    var targets = [];
    groups.forEach(function (group) {
      var children = Array.prototype.slice.call(group.children);
      children.forEach(function (child, i) {
        child.setAttribute('data-frp-reveal', '');
        child.style.transitionDelay = Math.min(i * 70, 560) + 'ms';
        targets.push(child);
      });
    });
    singles.forEach(function (el) {
      el.setAttribute('data-frp-reveal', '');
      targets.push(el);
    });
    if (!targets.length) return;
    function show(el) {
      if (el.classList.contains('frp-in')) return;
      el.classList.add('frp-in');
      setTimeout(function () { el.style.transitionDelay = ''; }, 900);
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        show(entry.target);
        io.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -4% 0px' });
    targets.forEach(function (t) { io.observe(t); });
    // Safety net: if the observer never fires (scaled iframes, print, odd embeds), reveal anything in view on scroll.
    var sweep = debounce(function () {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      targets.forEach(function (t) {
        if (t.classList.contains('frp-in')) return;
        var r = t.getBoundingClientRect();
        if (r.top < vh && r.bottom > 0) show(t);
      });
    }, 120);
    window.addEventListener('scroll', sweep, { passive: true });
    window.addEventListener('resize', sweep);
    setTimeout(sweep, 800);
    setTimeout(function () { targets.forEach(show); }, 6000);
  }

  /* ---------- Tables inside long-form content scroll instead of overflowing ---------- */
  function initTables() {
    all('.frp-prose table').forEach(function (table) {
      if (table.parentElement && table.parentElement.classList.contains('frp-table-wrap')) return;
      var wrap = document.createElement('div');
      wrap.className = 'frp-table-wrap';
      wrap.setAttribute('role', 'region');
      wrap.setAttribute('aria-label', table.getAttribute('aria-label') || 'Table');
      wrap.tabIndex = 0;
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
    });
  }

  /* ---------- In-page navigation built from headings ---------- */
  function initToc() {
    all('[data-frp-toc]').forEach(function (nav) {
      var source = document.querySelector(nav.getAttribute('data-frp-toc'));
      var wrapper = nav.closest('[data-frp-toc-wrap]') || nav;
      var min = parseInt(nav.getAttribute('data-frp-toc-min') || '2', 10);
      var depth = nav.getAttribute('data-frp-toc-depth') === '3' ? 'h2, h3' : 'h2';
      if (!source) { wrapper.hidden = true; return; }
      var headings = all(depth, source).filter(function (h) { return h.textContent.trim(); });
      if (headings.length < min) { wrapper.hidden = true; return; }
      var list = nav.querySelector('ul') || nav.appendChild(document.createElement('ul'));
      list.className = 'frp-toc';
      list.innerHTML = '';
      var used = {};
      headings.forEach(function (h) {
        if (!h.id) {
          var base = slugify(h.textContent), id = base, n = 2;
          while (document.getElementById(id) || used[id]) { id = base + '-' + n++; }
          h.id = id;
        }
        used[h.id] = true;
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = '#' + h.id;
        a.textContent = h.textContent.trim();
        if (h.tagName === 'H3') li.className = 'frp-toc__sub';
        li.appendChild(a);
        list.appendChild(li);
      });
      wrapper.hidden = false;

      // Scroll spy
      var links = all('a', list);
      var stickyTop = 0;
      function measure() {
        var raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
        stickyTop = (parseFloat(raw) || 90) + 40;
      }
      measure();
      var ticking = false;
      function update() {
        ticking = false;
        var current = null;
        var vh = window.innerHeight || document.documentElement.clientHeight;
        for (var i = 0; i < headings.length; i++) {
          if (headings[i].getBoundingClientRect().top - stickyTop <= 0) current = headings[i]; else break;
        }
        if (!current && headings[0].getBoundingClientRect().top < vh * 0.6) current = headings[0];
        links.forEach(function (a) {
          var active = current && a.getAttribute('href') === '#' + current.id;
          a.classList.toggle('is-active', !!active);
          if (active) a.setAttribute('aria-current', 'location'); else a.removeAttribute('aria-current');
        });
      }
      window.addEventListener('scroll', function () {
        if (!ticking) { ticking = true; setTimeout(update, 60); }
      }, { passive: true });
      window.addEventListener('resize', debounce(function () { measure(); update(); }, 150));
      update();
    });
  }

  /* ---------- Accordion (details/summary with smooth height) ---------- */
  function initAccordions() {
    all('.frp-acc__item').forEach(function (item) {
      var summary = item.querySelector('summary');
      var panel = item.querySelector('.frp-acc__panel');
      if (!summary || !panel || item.__frp) return;
      item.__frp = true;
      var group = item.closest('[data-frp-one]');
      var animating = false;

      function closeOthers() {
        if (!group) return;
        all('.frp-acc__item[open]', group).forEach(function (other) {
          if (other !== item) toggle(other, false);
        });
      }

      function toggle(target, open) {
        var p = target.querySelector('.frp-acc__panel');
        if (reduceMotion || !p.animate) { target.open = open; return; }
        if (open) {
          target.open = true;
          var h = p.scrollHeight;
          p.style.overflow = 'hidden';
          p.animate({ height: ['0px', h + 'px'], opacity: [0, 1] }, { duration: 280, easing: 'cubic-bezier(.2,.7,.2,1)' })
            .onfinish = function () { p.style.overflow = ''; };
        } else {
          var start = p.scrollHeight;
          p.style.overflow = 'hidden';
          var anim = p.animate({ height: [start + 'px', '0px'], opacity: [1, 0] }, { duration: 220, easing: 'cubic-bezier(.2,.7,.2,1)' });
          anim.onfinish = function () { target.open = false; p.style.overflow = ''; };
        }
      }

      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (animating) return;
        animating = true;
        setTimeout(function () { animating = false; }, 300);
        if (item.open) { toggle(item, false); } else { closeOthers(); toggle(item, true); }
      });
    });

    // Deep links: open the matching question or group target
    function openFromHash() {
      var hash = decodeURIComponent(location.hash || '').slice(1);
      if (!hash) return;
      var target = document.getElementById(hash);
      if (!target) return;
      var item = target.closest ? target.closest('.frp-acc__item') : null;
      if (item && !item.open) item.open = true;
    }
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
  }

  /* ---------- Client-side filtering (blog search, FAQ search) ---------- */
  function initFilters() {
    all('[data-frp-filter]').forEach(function (root) {
      var input = root.querySelector('[data-frp-filter-input]');
      if (!input) return;
      var clear = root.querySelector('[data-frp-filter-clear]');
      var items = all('[data-frp-item]', root);
      var groups = all('[data-frp-group]', root);
      var count = root.querySelector('[data-frp-count]');
      var empty = root.querySelector('[data-frp-empty]');
      var emptyTerm = root.querySelector('[data-frp-empty-term]');
      var searchAll = root.querySelector('[data-frp-search-all]');
      var hideWhenFiltering = all('[data-frp-hide-on-filter]', root);
      var openMatches = root.hasAttribute('data-frp-open-matches');
      var total = items.length;
      var baseSearchAll = searchAll ? searchAll.getAttribute('href') : '';

      items.forEach(function (item) {
        if (!item.getAttribute('data-frp-text')) {
          item.setAttribute('data-frp-text', item.textContent.replace(/\s+/g, ' ').trim().toLowerCase());
        }
      });

      function apply() {
        var q = input.value.trim().toLowerCase();
        var terms = q.split(/\s+/).filter(Boolean);
        var shown = 0;
        items.forEach(function (item) {
          var text = item.getAttribute('data-frp-text');
          var match = !terms.length || terms.every(function (t) { return text.indexOf(t) !== -1; });
          item.hidden = !match;
          if (match) shown++;
          if (openMatches && item.classList.contains('frp-acc__item')) {
            if (terms.length && match) item.open = true;
            if (!terms.length) item.open = false;
          }
        });
        groups.forEach(function (group) {
          var visible = all('[data-frp-item]', group).some(function (i) { return !i.hidden; });
          group.hidden = !visible;
        });
        hideWhenFiltering.forEach(function (el) { el.hidden = !!terms.length; });
        if (clear) clear.hidden = !q;
        if (count) {
          var template = terms.length ? count.getAttribute('data-frp-count-query') : count.getAttribute('data-frp-count-template');
          count.textContent = fill(template, { n: shown, t: total, q: input.value.trim() });
        }
        if (empty) empty.hidden = !(terms.length && shown === 0);
        if (emptyTerm) emptyTerm.textContent = input.value.trim();
        if (searchAll) {
          searchAll.href = baseSearchAll + (baseSearchAll.indexOf('?') === -1 ? '?' : '&') + 'q=' + encodeURIComponent(input.value.trim());
        }
        root.classList.toggle('is-filtering', !!terms.length);
      }

      input.addEventListener('input', debounce(apply, 110));
      input.addEventListener('search', apply);
      if (clear) {
        clear.addEventListener('click', function () {
          input.value = '';
          apply();
          input.focus();
        });
      }
      var form = input.closest('form');
      if (form && form.hasAttribute('data-frp-filter-form')) {
        form.addEventListener('submit', function (e) { e.preventDefault(); apply(); });
      }
      apply();
    });
  }

  /* ---------- Share controls ---------- */
  function initShare() {
    all('[data-frp-share]').forEach(function (root) {
      var url = root.getAttribute('data-url') || location.href;
      var title = root.getAttribute('data-title') || document.title;
      var status = root.querySelector('[data-frp-share-status]');
      var nativeBtn = root.querySelector('[data-frp-share-action="native"]');
      var copyBtn = root.querySelector('[data-frp-share-action="copy"]');

      function say(message) {
        if (!status) return;
        status.textContent = message;
        clearTimeout(status.__t);
        status.__t = setTimeout(function () { status.textContent = ''; }, 2600);
      }

      if (nativeBtn) {
        if (navigator.share) {
          nativeBtn.hidden = false;
          nativeBtn.addEventListener('click', function () {
            navigator.share({ title: title, url: url }).catch(function () {});
          });
        } else {
          nativeBtn.hidden = true;
        }
      }
      if (copyBtn) {
        copyBtn.addEventListener('click', function () {
          var done = function () { say(copyBtn.getAttribute('data-frp-copied') || 'Link copied'); };
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(url).then(done, function () { fallbackCopy(url, done); });
          } else {
            fallbackCopy(url, done);
          }
        });
      }
    });

    function fallbackCopy(text, done) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); done(); } catch (e) {}
      document.body.removeChild(ta);
    }
  }

  /* ---------- Form validation (accessible, additive to Shopify's server errors) ---------- */
  function initForms() {
    all('form[data-frp-validate]').forEach(function (form) {
      var summary = form.querySelector('[data-frp-form-summary]');
      var summaryList = summary ? summary.querySelector('ul') : null;
      var fields = all('[data-frp-required], [type="email"]', form);
      var emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

      function wrapperOf(field) { return field.closest('.frp-field'); }

      function validate(field) {
        var wrap = wrapperOf(field);
        if (!wrap) return true;
        var error = wrap.querySelector('.frp-field__error');
        var value = field.value.trim();
        var message = '';
        if (field.hasAttribute('data-frp-required') && !value) {
          message = field.getAttribute('data-frp-error-required') || 'This field is required.';
        } else if (field.type === 'email' && value && !emailRe.test(value)) {
          message = field.getAttribute('data-frp-error-email') || 'Enter a valid email address.';
        }
        wrap.classList.toggle('is-invalid', !!message);
        field.setAttribute('aria-invalid', message ? 'true' : 'false');
        if (error) {
          var text = error.querySelector('[data-frp-error-text]') || error;
          text.textContent = message;
          error.hidden = !message;
          if (message) field.setAttribute('aria-describedby', error.id); else if (field.getAttribute('aria-describedby') === error.id) field.removeAttribute('aria-describedby');
        }
        return !message;
      }

      fields.forEach(function (field) {
        field.addEventListener('blur', function () { if (field.value.trim() || wrapperOf(field).classList.contains('is-invalid')) validate(field); });
        field.addEventListener('input', function () { if (wrapperOf(field) && wrapperOf(field).classList.contains('is-invalid')) validate(field); });
      });

      form.addEventListener('submit', function (e) {
        var invalid = fields.filter(function (f) { return !validate(f); });
        if (!invalid.length) {
          var btn = form.querySelector('[type="submit"]');
          if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; setTimeout(function () { btn.disabled = false; btn.removeAttribute('aria-busy'); }, 8000); }
          if (summary) summary.hidden = true;
          return;
        }
        e.preventDefault();
        if (summary && summaryList) {
          summaryList.innerHTML = '';
          invalid.forEach(function (f) {
            var label = wrapperOf(f).querySelector('.frp-field__label');
            var li = document.createElement('li');
            var a = document.createElement('a');
            a.href = '#' + f.id;
            a.textContent = label ? label.textContent.replace(/\s*\(optional\)\s*/i, '').trim() : f.name;
            a.addEventListener('click', function (ev) { ev.preventDefault(); f.focus(); });
            li.appendChild(a);
            summaryList.appendChild(li);
          });
          summary.hidden = false;
          summary.focus();
        } else {
          invalid[0].focus();
        }
      });
    });
  }

  /* ---------- Mobile in-page nav mirrors the desktop list ---------- */
  function initTocMirror() {
    all('[data-frp-toc-mirror]').forEach(function (mirror) {
      var source = document.querySelector(mirror.getAttribute('data-frp-toc-mirror'));
      var wrap = mirror.closest('[data-frp-toc-wrap]') || mirror;
      if (!source || source.hidden) { wrap.hidden = true; return; }
      var list = source.querySelector('ul.frp-toc');
      if (!list || !list.children.length) { wrap.hidden = true; return; }
      var clone = list.cloneNode(true);
      mirror.innerHTML = '';
      mirror.appendChild(clone);
      wrap.hidden = false;
      var details = mirror.closest('details');
      if (details) {
        all('a', clone).forEach(function (a) { a.addEventListener('click', function () { details.open = false; }); });
      }
    });
  }

  function init() {
    initTables();
    initToc();
    initTocMirror();
    initAccordions();
    initFilters();
    initShare();
    initForms();
    initReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-run when a section is (re)loaded
  document.addEventListener('shopify:section:load', function () { init(); });
})();
